import { useMutation, useQueryClient } from "react-query";
import { submitFeatureFeedback, FeatureFeedbackCreateInput } from "api/feedback";
import { GET_DISPLAY_FEEDBACK_DIALOG } from "@constants/reactQueryKeys";

interface SubmitFeatureFeedbackParams {
  userId: string;
  data: FeatureFeedbackCreateInput;
}

/**
 * Hook for submitting feature feedback
 * 
 * Sends user feedback to the backend and invalidates the feedback dialog query
 * to update its 1/4 chance of displaying at the next feedback opportunity.
 * 
 * @returns {Object} Mutation object with submitFeedback function
 * 
 * @example
 * const { submitFeedback } = useSubmitFeatureFeedback();
 * 
 * const handleSubmit = (formData) => {
 *   submitFeedback({
 *     userId: currentUser.id,
 *     data: formData
 *   });
 * };
 */
export const useSubmitFeatureFeedback = () => {
  const queryClient = useQueryClient();

  const { mutate: submitFeedback, ...rest } = useMutation(
    ({ userId, data }: SubmitFeatureFeedbackParams) => submitFeatureFeedback(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_DISPLAY_FEEDBACK_DIALOG]);
      },
      onError: (error: Error) => {
        console.error("Error submitting feature feedback:", error);
      },
    }
  );

  return {
    submitFeedback,
    ...rest,
  };
};